/* We do like the old way to use simple filters, this makes it easier to use them in the templates
 *
 * <template>
 *  <div>{{ $filters.currencyUSD(100) }}</div>
 * </template>
 */

import { App, Plugin } from 'vue';
import moment from 'moment';

import { formatCurrency, formatMonth, formatMonthShort, formatPercent } from './utils/format';

// Register global filters
export const $filters = {
	currency(value?: string | number | null, currency: string = 'EUR') {
		if (value === null || value === undefined || value === '') {
			return '';
		}
		const numberValue = typeof value === 'number' ? value : Number.parseFloat(value);
		if (Number.isNaN(numberValue)) {
			return '';
		}
		if (currency === 'USD') {
			return '$' + numberValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		}
		return currency === 'EUR' ?
				formatCurrency(numberValue)
			:	numberValue.toLocaleString('de-DE', { style: 'currency', currency: currency });
	},
	percent(value?: string | number | null, maxDigits: number = 3) {
		if (value === null || value === undefined || value === '') {
			return '';
		}
		return formatPercent(value, maxDigits);
	},
	date(value?: string | null) {
		if (!value) {
			return '';
		}
		const t = moment(value);
		return t.isValid() ? t.format('DD.MM.YYYY') : '';
	},
	month(value?: string | null) {
		return value ? formatMonth(value) : '';
	},
	monthShort(value?: string | null) {
		return value ? formatMonthShort(value) : '';
	},
	datetime(value?: string | null) {
		if (!value) {
			return '';
		}
		const t = moment(value);
		return t.isValid() ? t.format('DD.MM.YYYY HH:mm') : '';
	},
	time(value?: string | null) {
		if (!value) {
			return '';
		}
		const t = moment(value);
		return t.isValid() ? t.format('HH:mm') : '';
	},
	date2data(from?: string | null, to?: string | null) {
		return `${$filters.date(from)} - ${$filters.date(to)}`;
	},
	time2time(from?: string | null, to?: string | null) {
		if (!from && !to) return '';

		const mFrom = from && moment(from);
		const mTo = to && moment(to);

		if (mFrom && !mFrom.isValid()) return '';
		if (mTo && !mTo.isValid()) return '';

		const base = mFrom || mTo;
		const date = base ? base.format('DD.MM.') : '';

		const start = mFrom ? mFrom.format('HH:mm') : '';
		const end = mTo ? mTo.format('HH:mm') : '';

		return start && end ? `${date} ${start} – ${end}` : `${date} ${start || end}`;
	},
	minutes2duration(minutes: number | null | undefined): string {
		if (minutes === null || minutes === undefined) return '';
		// Counted, not read off a clock: a month's hours go past 24
		const abs = Math.round(Math.abs(minutes));
		const formatted = `${Math.floor(abs / 60)}h ${String(abs % 60).padStart(2, '0')}m`;
		return minutes < 0 ? `-${formatted}` : formatted;
	},
	distance_km(distance?: string | number | null) {
		if (!distance) return '';
		const value = Number(distance);
		return Number.isFinite(value) ? `${Math.round(value / 1000)} km` : '';
	},
	file_size(value: string | number | null) {
		if (value === null || value === undefined || value === '') {
			return '';
		}
		let numberValue = typeof value === 'number' ? value : Number.parseFloat(value);
		if (Number.isNaN(numberValue)) {
			return '';
		}
		const units = ['B', 'kB', 'MB', 'GB', 'TB'];
		let i = 0;
		while (numberValue >= 1024 && i < units.length - 1) {
			numberValue /= 1024;
			i++;
		}
		return numberValue.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' ' + units[i];
	},
	unit(value: string | number | null, unit: string = '', precision: number = 2) {
		if (value === null || value === undefined || value === '') {
			return '';
		}
		const numberValue = typeof value === 'number' ? value : Number.parseFloat(value);
		if (Number.isNaN(numberValue)) {
			return '';
		}
		return (
			numberValue.toLocaleString('de-DE', {
				minimumFractionDigits: precision,
				maximumFractionDigits: precision
			}) + (unit ? ' ' + unit : '')
		);
	}
};

const ApplicationPlugin: Plugin = {
	install: (app: App) => {
		// Add $filters to globalProperties
		app.config.globalProperties.$filters = $filters;
	}
};

export default ApplicationPlugin;

declare module '@vue/runtime-core' {
	export interface ComponentCustomProperties {
		$filters: typeof $filters;
	}
}
